import React, { useContext, useMemo } from 'react';
import { Modal, Box, Typography, Button, Divider, Table, TableRow, TableCell, TableContainer, TableBody, TableHead, Tooltip } from '@mui/material';
import { ThemeContext } from '../../context/ThemeContext';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import BidValue from '../global/BidAsk/BidValue';
import High from '../global/BidAsk/High';
import AskValue from '../global/BidAsk/AskValue';
import Low from '../global/BidAsk/Low';
import SpreadValue from '../global/BidAsk/SpreadValue';

const SymbolDetailsModal = ({ open, closeModal }) => {
  const { darkMode } = useContext(ThemeContext);
  const selectedSymbol = useSelector(state => {
    const id = state.symbol.selectedSymbol.symbolId;
    return state.watchlist.symbols?.[id] || null;
  });

  const { t } = useTranslation()
  const symbolData = useSelector(state => state.updateSymbol.symbols?.[selectedSymbol?.id])
  const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams);
  const symbolParameterData = allSymbolParams?.[selectedSymbol?.id] || null;
  const unit = useSelector(state => state.groupUnit.unit);
  const group = useSelector(state => state.groupUnit.group);
  const digit = symbolData?.digits
  const symbolGroup = group.find((g) => g.id === symbolData?.parentId) || null;
  const amountUnit = useMemo(() => {
    if (!unit?.length || !symbolData?.amountUnitId) return null;
    return unit.find((u) => u.id === symbolData.amountUnitId) || null;
  }, [unit, symbolData?.amountUnitId]);

  const getWeekdayName = (dayNumber) => {
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return weekdays[dayNumber] || "Invalid Day";
  };
  const getCommissionTypeString = (commission) => {
    if (commission === null || commission === undefined || commission === 0) {
      return t('noCommission');
    } else if (commission === 1) {
      return t('OfTurnover');
    } else if (commission === 2) {
      return t('perLot');
    } else if (commission === 3) {
      return t('perTrade');
    } else {
      return t('noCommission');
    }
  };



  return (
    <Modal open={open} closeModal={closeModal} aria-labelledby="symbol-details-modal" fullWidth maxWidth="lg">
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: darkMode ? "var(--background-color)" : 'var(--white)', width: {
          xs: '100%',  // full width on extra small screens (mobile)
          sm: '90%',   // slightly less on small screens
          md: '70%',   // medium screens
          lg: '45%',   // 45% on large and up
        },
      }}>
        <Box className="bg-primary" sx={{ padding: '8px', }}>
          <Typography id="symbol-details-modal" variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >
            {symbolData?.name || 'Symbol Details'}
          </Typography>
        </Box>
        <Box sx={{ padding: '16px', maxHeight: '70vh', overflow: 'auto', }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '16px', mt: 2, mb: 4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('symbolGroup')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolGroup?.name}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('tradeType')}</Typography>
              <Typography fontWeight={500} variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.tradeType === 0 ? t('fullTrade') : t('closeOnly')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('amountUnit')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{amountUnit?.description || 'N/A'}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('minAmount')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.minimumAmountPerDeal}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('weeklyOpen')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>  {`${getWeekdayName(symbolData?.tradingTimes?.weeklyOpenDay)} ${symbolData?.tradingTimes?.weeklyOpenFrom.substring(0, 5) || "N/A"}`}</Typography>

              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('limitOffset')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.limitOffset || 'N/A'} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('stopOffset')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.stopOffset || 'N/A'} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('maximumQuantity')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.maxQuantity || "None"} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('initialMargin')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.initialMargin}   {symbolParameterData?.initialMarginType === 1 ? ' %' : ''} </Typography>
            </Box>

            <Box sx={{}}></Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('spread')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}><SpreadValue symbolId={symbolData?.id} /></Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('contractSize')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.contractSize ?? 0}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('digit')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.digits || 'N/A'} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('expiryDate')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{!symbolData?.tradingTimes?.expiryDate || symbolData.tradingTimes.expiryDate === '0001-01-01' ? 'Missing' : symbolData.tradingTimes.expiryDate}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t("maxAmount")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.maximumAmountPerDeal}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t("weeklyClose")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>  {`${getWeekdayName(symbolData?.tradingTimes?.weeklyCloseDay)} ${symbolData?.tradingTimes?.weeklyCloseTo.slice(0, 5) || "N/A"}`}</Typography>

              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('tradeStatus')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolData?.tradeStatus === 0 ? t('open') : t('closed')} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('openCommissionType')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>  {getCommissionTypeString(symbolParameterData?.openCommissionType)}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('closeCommissionType')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{getCommissionTypeString(symbolParameterData?.closeCommissionType)} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('openCommission')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.openCommissionValue} </Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('closeCommission')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? "var(--white)" : "var(--black)", textAlign: 'right' }}>{symbolParameterData?.closeCommissionValue} </Typography>
            </Box>
          </Box>
          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', mb: 2, mt: 2, bgcolor: darkMode ? '#1a2133' : 'var(--white)', borderRadius: 1, }}>

            <TableContainer sx={{ overflowX: "auto", pt: 0.5 }}>
              <Table sx={{ minWidth: 500, textAlign: 'left' }} aria-label="weekdays table">
                <TableHead sx={{ padding: 0 }}>
                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                      <TableCell key={index} sx={{ border: 'none', whiteSpace: "nowrap", fontWeight: 'bold', color: darkMode ? 'var(--white)' : 'var(--black)', p: 0 }}>
                        <Typography variant='body2' sx={{ marginLeft: '6px', mb: 1 }}>{t(day)}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      const dailyClosings = symbolData?.tradingTimes?.dailyClosingTimes ?? [];

                      return (
                        <TableCell key={index} sx={{ p: 0, border: 'none', position: 'relative', height: '16px' }}>
                          <div style={{ height: '8px', width: '93%', backgroundColor: '#007bff', borderRadius: '4px', margin: '0 auto', opacity: isWithinRange ? 1 : 0.2 }} />

                          {isWithinRange && dailyClosings.map((closing, i) => {
                            const fromHour = parseInt(closing.from.split(':')[0], 10);
                            const toHour = parseInt(closing.to.split(':')[0], 10);
                            const left = (fromHour / 24) * 100;
                            const width = ((toHour - fromHour) / 24) * 100;

                            return (
                              <Tooltip key={i} title={<> <strong>{t('dailyMarketClosure')}</strong><br /> {t('time')}: {closing.from.slice(0, 5)} - {closing.to.slice(0, 5)}</>} arrow>
                                <div style={{ position: 'absolute', top: '4px', left: `${left}%`, width: `${width}%`, height: '8px', backgroundColor: '#0141a0', borderRadius: '4px', opacity: 0.7, cursor: 'pointer' }} />
                              </Tooltip>
                            );
                          })}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((_, index) => {
                      const openDay = symbolData?.tradingTimes?.weeklyOpenDay ?? -1;
                      const closeDay = symbolData?.tradingTimes?.weeklyCloseDay ?? -1;
                      const openFrom = symbolData?.tradingTimes?.weeklyOpenFrom;
                      const closeTo = symbolData?.tradingTimes?.weeklyCloseTo;

                      const isWithinRange = openDay <= closeDay
                        ? index >= openDay && index <= closeDay
                        : index >= openDay || index <= closeDay;

                      const openTime = openFrom ? openFrom.slice(0, 5) : '00:00';
                      const closeTime = closeTo ? closeTo.slice(0, 5) : '00:00';
                      const timeRange = `${openTime} - ${closeTime}`;

                      return (
                        <TableCell key={index} sx={{ border: 'none', padding: '5px', whiteSpace: "nowrap", color: darkMode ? 'var(--white)' : 'var(--black)', fontSize: '13px', textAlign: 'center', }}>
                          <Tooltip arrow title={
                            isWithinRange ? (
                              <>
                                <strong>{t('marketOpenTime')}:</strong> {openTime}<br />
                                <strong>{t('marketCloseTime')}:</strong> {closeTime}
                              </>
                            ) : `${t('marketClosed')}`
                          }
                          >
                            <span style={{ cursor: 'pointer' }}>{isWithinRange ? timeRange : '00:00 - 00:00'}</span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>

                </TableBody>
              </Table>
            </TableContainer>

          </Box>

          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '16px', mt: 2, mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('bid')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><BidValue symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('high')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><High symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t("open")}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}>  {symbolData?.open ? symbolData.open?.toFixed(digit) : "0.00"}</Typography>
            </Box>
            <Box sx={{}}></Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('ask')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><AskValue symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('low')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}><Low symbolId={symbolData.id} digits={digit} /></Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--secondaryLight1)' : 'var(--borderDark)' }}>{t('close')}</Typography>
              <Typography variant='body2' sx={{ color: darkMode ? 'var(--white)' : 'var(--black)', textAlign: 'right' }}>{symbolData?.close ? symbolData?.close.toFixed(digit) : "0.00"}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={closeModal} sx={{ m: 2, alignSelf: 'center' }} variant="outlined">
            {t('close')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SymbolDetailsModal;